import { ArrowSquareOut, Camera, MapPinSimple } from '@phosphor-icons/react';
import { Button, VText } from '@vake/ui-kit';
import { Feature } from 'geojson';
import { FC } from 'react';
import styled from 'styled-components';
import { getLegendDot } from '../../../shared/components/object-class-dot.tsx';
import { DetectionImage, PlanetThumbnail, TargetOfInterestImage } from '../../../shared/components/Popup.tsx';
import { VakeClipboard } from '../../../utils/clipboard.tsx';
import { formatLatLonDMText, formatLatLonText } from '../../../utils/date.ts';
import { EOBrowserService, getEOBrowserUrl } from '../../../utils/eobrowser.ts';
import { mmsiToFlag } from '../../../utils/mmsi-to-flag.tsx';
import { externalVesselUrl } from '../../../utils/mmsiExternalCall.ts';
import { VakeShareButton } from '../../../utils/shareButton.tsx';
import { Arrow } from '../interactiveReport/reportInfo/ReportInfo.tsx';
import { BorderFreeRounded } from '../styles/Wrappers.tsx';
import { ManuallyValidate } from './ManuallyValidate.tsx';
import { SendToMattermost } from './send-to-mattermost.tsx';
import { TagView } from './tags.tsx';

const FocusedImageWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    aligh-items: center;
`;

const FocusedImage = styled.div`
    display: flex;
    width: 100%;
    aspect-ratio: 1/1;
`;

const InformationContainer = styled.div`
    margin: 5px 5px 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 12px;
`;

const Information = styled.div`
    padding: 10px;
    background-color: rgba(255, 255, 255, 0.05);
    display: flex;
    flex-direction: row;
    border-radius: 10px;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
`;

const Banner = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const SpaceOccuiperContainer = styled.div`
    display: flex;
    gap: 8px;
`;

export const VesselInfo: FC<{
    selectedFeature: Feature | null;
    setSelectedFeature: (value: Feature | null) => void;
    upsampleImage?: boolean;
    hideClearview?: boolean;
    hideOrderReportImage?: boolean;
    hideNavigation?: boolean;
}> = ({ selectedFeature, setSelectedFeature, upsampleImage, hideClearview, hideOrderReportImage, hideNavigation }) => {
    const date = selectedFeature!.properties!.timestamp.slice(0, 10);

    const getUrl = (service: EOBrowserService) => {
        return getEOBrowserUrl({
            date: date,
            lat: selectedFeature!.properties!.lat,
            lng: selectedFeature!.properties!.lon,
            imageSource: selectedFeature!.properties!.image_source,
            service: service,
            zoom: 15,
        });
    };

    const formatTimestamp = (timestamp: string) => {
        const date = timestamp.split(' ');
        const datestring = date[1] + ' ' + date[2] + ' ' + date[3] + ', ' + date[4].slice(0, 5) + ' GMT';
        return datestring;
    };
    const name = selectedFeature?.properties?.name ? selectedFeature.properties.name : null;
    const shipType = selectedFeature?.properties?.ship_type ? selectedFeature.properties.ship_type : null;
    const mmsi = selectedFeature?.properties?.mmsi ? selectedFeature.properties.mmsi : null;
    const imo = selectedFeature?.properties?.imo ? selectedFeature.properties.imo : null;
    const formattedLatLonText = formatLatLonText(selectedFeature);
    const formattedLatLonDMText = formatLatLonDMText(selectedFeature);
    return (
        <div className="flex max-h-[calc(100vh-16vh)]">
            <BorderFreeRounded>
                <Banner>
                    {!hideNavigation && (
                        <Arrow
                            onClick={() => {
                                setSelectedFeature(null);
                            }}
                        >
                            <img src="/images/icons/ArrowLeft.svg" alt="arrow left" />
                        </Arrow>
                    )}
                    <VText>{selectedFeature?.properties?.name}</VText>
                    <VakeShareButton shareValue={window.location.href} />
                    {mmsi ? (
                        <ArrowSquareOut
                            className="cursor-pointer"
                            size={20}
                            color="white"
                            onClick={() => window.open(externalVesselUrl(mmsi), '_blank')}
                        />
                    ) : null}
                </Banner>
                {selectedFeature && selectedFeature.properties ? (
                    <div className="flex-grow overflow-y-auto">
                        <FocusedImageWrapper>
                            <FocusedImage>
                                {selectedFeature?.properties?.image_source !== 'planet_scope' ? (
                                    selectedFeature.properties.image_path ? (
                                        <DetectionImage image_path={selectedFeature.properties.image_path} />
                                    ) : (
                                        <TargetOfInterestImage
                                            imageSource={selectedFeature.properties.image_source}
                                            lon={selectedFeature.properties.lon}
                                            lat={selectedFeature.properties.lat}
                                            timestamp={selectedFeature.properties.timestamp}
                                            mmsi={selectedFeature.properties.mmsi}
                                            upsampleImage={upsampleImage || false}
                                        />
                                    )
                                ) : selectedFeature.properties.image_path ? (
                                    <PlanetThumbnail image_path={selectedFeature.properties.image_path} />
                                ) : null}
                            </FocusedImage>
                        </FocusedImageWrapper>
                        <InformationContainer>
                            <SpaceOccuiperContainer>
                                <TagView tags={selectedFeature.properties?.tags} />
                            </SpaceOccuiperContainer>
                            <SpaceOccuiperContainer>
                                {selectedFeature?.properties?.matching_label &&
                                    selectedFeature?.properties?.object_class && (
                                        <Information>
                                            {getLegendDot(selectedFeature)}
                                            <VText variant="D1">{`${selectedFeature.properties.matching_label} ${selectedFeature.properties.object_class}`}</VText>
                                        </Information>
                                    )}
                            </SpaceOccuiperContainer>
                            {name && (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>Name: {name}</VText>
                                        <VakeClipboard infoValue={name} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            )}
                            {imo && (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>IMO {imo}</VText>
                                        <VakeClipboard infoValue={imo} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            )}
                            {mmsi && (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <div className="flex gap-2 items-center">
                                            <VText>MMSI</VText>
                                            <VText>{mmsi ? mmsiToFlag(mmsi) : null}</VText>
                                            <VText>{mmsi}</VText>
                                        </div>
                                        <VakeClipboard infoValue={mmsi} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            )}
                            {shipType && (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>Type: {shipType}</VText>
                                        <VakeClipboard infoValue={shipType} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            )}
                            {typeof selectedFeature.properties.image_source === 'string' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <img
                                            src="/images/icons/satellite.svg"
                                            alt="no source"
                                            onDoubleClick={() => window.open(getUrl('copernicus'), '_blank')}
                                        />
                                        <VText>Satellite: {selectedFeature.properties?.image_source}</VText>
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {formattedLatLonText ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>Lat, Lon: {formattedLatLonText}</VText>
                                        <VakeClipboard infoValue={formattedLatLonText} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {formattedLatLonDMText ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <MapPinSimple color="white" size={16} />
                                        <VText>{formattedLatLonDMText}</VText>
                                        <VakeClipboard infoValue={formattedLatLonDMText} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {typeof selectedFeature.properties?.est_speed === 'number' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <img src="/images/icons/speedometer.svg" alt="no speed" />
                                        <VText>AIS Speed: {selectedFeature.properties?.est_speed} kn</VText>
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {typeof selectedFeature.properties?.ais_length === 'number' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>AIS Length: {selectedFeature.properties?.ais_length} m</VText>
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {typeof selectedFeature.properties?.length === 'number' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>Predicted Length: {selectedFeature.properties?.length} m</VText>
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {typeof selectedFeature.properties?.object_class_score == 'number' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <VText>
                                            Confidence:{' '}
                                            {Math.round(selectedFeature.properties?.object_class_score * 100)}%
                                        </VText>
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {typeof selectedFeature.properties?.timestamp === 'string' ? (
                                <SpaceOccuiperContainer>
                                    <Information>
                                        <img src="/images/icons/calendar.svg" alt="no timestamp" />
                                        <VText>
                                            {formatTimestamp(
                                                new Date(selectedFeature.properties?.timestamp).toUTCString()
                                            )}
                                        </VText>
                                        <VakeClipboard infoValue={selectedFeature.properties?.timestamp} />
                                    </Information>
                                </SpaceOccuiperContainer>
                            ) : null}
                            {selectedFeature.properties &&
                                typeof selectedFeature.properties?.image_path === 'string' &&
                                selectedFeature.properties?.image_source === 'sentinel2' &&
                                !hideClearview && (
                                    <Button
                                        variant="neutral"
                                        onClick={() => {
                                            if (selectedFeature.properties) {
                                                const url =
                                                    window.location.origin +
                                                    '/clearview?insight_search_uuid=' +
                                                    JSON.stringify([
                                                        selectedFeature.properties.image_path
                                                            .split('sentinel2/')[1]
                                                            .split('.png')[0],
                                                    ]);
                                                window.open(url, '_blank');
                                            }
                                        }}
                                        text="Use VAKE ClearView"
                                    />
                                )}
                            {selectedFeature.properties && !hideOrderReportImage && (
                                <SendToMattermost
                                    textOnButton="Order Report Image"
                                    iconOnButton={<Camera size={16} />}
                                    successMessage="Thank you, we've received your order. We'll send you the image soon."
                                    textToSend={`I want a report image for insight uuid ${selectedFeature.properties.uuid} with timestamp ${selectedFeature.properties.timestamp}. [Link to insight](${window.location.href})`}
                                />
                            )}
                            <ManuallyValidate selectedFeature={selectedFeature} date={date} />
                        </InformationContainer>
                    </div>
                ) : (
                    <InformationContainer>
                        <div style={{ height: '50px' }}>missing image information</div>
                    </InformationContainer>
                )}
            </BorderFreeRounded>
        </div>
    );
};
